import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useCallback, type ReactNode } from 'react';
import type { Station } from '@shared/types';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useStations } from './hooks/useStations';
import { useProgram } from './hooks/useProgram';
import Login from './pages/Login';
import ProgramSelect from './pages/ProgramSelect';
import StationSelect from './pages/StationSelect';
import RequestForm from './pages/RequestForm';
import MyRequests from './pages/MyRequests';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import OrderPrint from './pages/OrderPrint';
import ItemsVendors from './pages/ItemsVendors';
import AdminPanel from './pages/AdminPanel';
import Layout from './components/Layout';
import PublicLayout from './components/PublicLayout';
import './index.css';

const ROLE_RANK = { crew: 0, logistics: 1, admin: 2 } as const;

function LoadingScreen() {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-surface text-white">
      <div className="text-center">
        <img src="/dcvfd-badge.svg" alt="DCVFD" className="h-16 w-auto mx-auto mb-4 animate-pulse" />
        <p className="text-zinc-500 text-sm">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Program → station → request form. Shared by the public page and the
 * signed-in shell; only who the request is attributed to differs.
 */
function RequestFlow({ signedInAs }: { signedInAs: { name: string; email: string } | null }) {
  const { selectedStation, selectStation, clearStation } = useStations();
  const { program, selectProgram, clearProgram } = useProgram();
  const [station, setStation] = useState<Station | null>(selectedStation);

  const handleSelectStation = useCallback(
    (s: Station) => {
      selectStation(s);
      setStation(s);
    },
    [selectStation],
  );

  const handleChangeStation = useCallback(() => {
    clearStation();
    setStation(null);
  }, [clearStation]);

  // Program first, then station: which catalog you're ordering from decides
  // what the form shows, so asking for it after the station would mean
  // re-rendering the whole item list.
  if (!program) return <ProgramSelect onSelect={selectProgram} />;
  if (!station) return <StationSelect onSelect={handleSelectStation} />;
  return (
    <RequestForm
      station={station}
      program={program}
      onChangeStation={handleChangeStation}
      onChangeProgram={clearProgram}
      signedInAs={signedInAs}
    />
  );
}

function AppShell() {
  const { user } = useAuth();

  const role = user!.role;
  const userRank = ROLE_RANK[role];
  // Signing in is how logistics and admin get to their screens, so land them
  // there; crew who sign in still start on the request form.
  const home = userRank >= ROLE_RANK.logistics ? '/dashboard' : '/request';

  return (
    <Layout role={role} userName={user!.name}>
      <Routes>
        <Route path="/request" element={<RequestFlow signedInAs={{ name: user!.name, email: user!.email }} />} />
        <Route path="/my-requests" element={<MyRequests />} />
        {userRank >= ROLE_RANK.logistics && <Route path="/dashboard" element={<Dashboard />} />}
        {userRank >= ROLE_RANK.logistics && <Route path="/orders" element={<Orders />} />}
        {userRank >= ROLE_RANK.logistics && <Route path="/orders/:id" element={<OrderDetail />} />}
        {userRank >= ROLE_RANK.logistics && <Route path="/items" element={<ItemsVendors />} />}
        {userRank >= ROLE_RANK.admin && <Route path="/admin" element={<AdminPanel />} />}
        <Route path="/login" element={<Navigate to={home} replace />} />
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
    </Layout>
  );
}

/** Screens that need a session: opened signed out, they go to sign-in rather than the form. */
const STAFF_PATHS = ['/dashboard', '/orders', '/orders/:id', '/items', '/admin', '/my-requests'];

/**
 * Signed out: the request form is the whole app, reached by a plain link. The
 * Settings link in its header leads to staff sign-in.
 */
function PublicShell() {
  return (
    <Routes>
      <Route
        path="/request"
        element={
          <PublicLayout>
            <RequestFlow signedInAs={null} />
          </PublicLayout>
        }
      />
      <Route path="/login" element={<Login />} />
      {STAFF_PATHS.map((p) => (
        <Route key={p} path={p} element={<Navigate to="/login" replace />} />
      ))}
      <Route path="*" element={<Navigate to="/request" replace />} />
    </Routes>
  );
}

function Root() {
  const { isLoading, user } = useAuth();
  if (isLoading) return <LoadingScreen />;
  return user ? <AppShell /> : <PublicShell />;
}

function AuthGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated || !user) return <Login />;

  return <>{children}</>;
}

function PrintRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user || (user.role !== 'logistics' && user.role !== 'admin')) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-white text-black">
        <p className="text-sm text-red-600">Forbidden — logistics access required.</p>
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route
            path="/orders/:id/print"
            element={
              <AuthGate>
                <PrintRoute>
                  <OrderPrint />
                </PrintRoute>
              </AuthGate>
            }
          />
          <Route path="*" element={<Root />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
